import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Euro as EuroIcon,
  InventoryOutlined as InventoryIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useMachineKPIs from '../hooks/useMachineKPIs';
import PeriodPicker from './PeriodPicker';
import { colors } from '../../../themes/typography';

const experimentColors = {
  pending: '#6B7280',
  accepted: '#3B82F6',
  running: '#10B981',
  manual_review: '#F59E0B',
};

const KPIChipsBar = ({ machineData, period, onPeriodChange }) => {
  const { t } = useTranslation();
  const kpis = useMachineKPIs(machineData);

  if (!machineData) return null;

  const baseChips = [
    {
      icon: <TrendingUpIcon sx={{ fontSize: 14 }} />,
      label: `${t('machines_chipSales')}: ${kpis.avgSales.toFixed(1)}u/${t('machines_chipDay')}`,
      title: t('machines_avgSales'),
      show: true
    },
    {
      icon: <EuroIcon sx={{ fontSize: 14 }} />,
      label: `${t('machines_chipMargin')}: €${kpis.avgMargin.toFixed(2)}/${t('machines_chipUnit')}`,
      title: t('machines_avgMargin'),
      show: true
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 14 }} />,
      label: `Stock: ${Math.round(kpis.stockFill * 100)}%`,
      title: t('machines_stockFill'),
      show: false
    },
  ];

  // Build per-status experiment pills
  const experimentPills = Object.entries(kpis.experimentsByStatus)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => {
      const color = experimentColors[status] || '#6B7280';
      return {
        icon: <ScienceIcon sx={{ fontSize: 14 }} />,
        label: `${count} ${t(status)}`,
        title: `${count} ${t(status)}`,
        show: true,
        statusColor: color,
      };
    });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {/* Period Pill */}
      {onPeriodChange && (
        <PeriodPicker period={period} onPeriodChange={onPeriodChange} />
      )}
      {baseChips.filter(c => c.show).map((chip, i) => (
        <Box
          key={i}
          title={chip.title}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            height: 26,
            px: '8px',
            borderRadius: '13px',
            fontSize: '11px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            border: `1px solid ${colors.border.medium}`,
            backgroundColor: 'transparent',
            color: colors.text.primary,
          }}
        >
          {React.cloneElement(chip.icon, {
            sx: { fontSize: 14, color: colors.text.secondary }
          })}
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', lineHeight: 1 }}>
            {chip.label}
          </Typography>
        </Box>
      ))}
      {experimentPills.map((pill, i) => (
        <Box
          key={`exp-${i}`}
          title={pill.title}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            height: 26,
            px: '8px',
            borderRadius: '13px',
            fontSize: '11px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            border: `1px solid ${pill.statusColor}4D`,
            backgroundColor: `${pill.statusColor}29`,
            color: pill.statusColor,
          }}
        >
          {React.cloneElement(pill.icon, {
            sx: { fontSize: 14, color: pill.statusColor }
          })}
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', lineHeight: 1 }}>
            {pill.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default React.memo(KPIChipsBar);
